import React, { useCallback, useState, useMemo, useContext } from 'react';
import { Trans } from 'react-i18next';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import arrayMutators from 'final-form-arrays';
import moment from 'moment';
import { ConfirmationModal, FormModal as BaseFormModal, Button } from '@ubeya/shared-web/components';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { API_DATE_FORMAT, API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as sharedAtoms from '@ubeya/shared/atoms/shared';
import { media } from '@ubeya/shared-web/style';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import { schedulingTimeRange } from 'shared/atoms/scheduling';
import EmployeeCard from 'shared/components/EmployeeCard/EmployeeCard';
import HourlyBookingFields from './HourlyBookingFields';
import { checkOverlappingHours } from 'scheduling/utils';

const FormModal = styled(BaseFormModal)`
  width: 90vw;
  max-height: 90vh;

  ${media.mobile`
      max-width: 700px;
    `}

  #modal-container {
    ${({ $isAttentionBox }) => $isAttentionBox && 'padding-top: 8px;'};
  }

  .submit-button {
    order: 5;
  }
  .cancel-button {
    ${({ $showDraftOption }) => $showDraftOption && 'margin-right: auto;'};
  }
  .delete-shift-button,
  .add-new-shift-button {
    margin-right: auto;
  }
`;

const DateText = styled.div`
  font-size: 18px;
  font-weight: 500;
  display: inline-flex;
  color: ${({ theme }) => theme.colors.primary};
`;

const HourlyBookingModal = ({
  onClose,
  disableDateChange = false,
  disableTimesChange = false,
  refetchEmployees,
  shiftsByEmployee,
  editMode,
  initialTimingValues,
  onDelete,
  showLabel = true,
  data,
  employeesMap,
  handleSubmit,
  openShiftsByDate,
  defaultTimePickerOptions: timePickerOptions,
  interval,
  renderExtraData,
  mappedLabels,
  showDraftOption = false,
  weekView,
  showShiftTags = false,
  ...modalProps
}) => {
  const { t } = useContext(ConfigContext);
  const { timeFormat, dateFormat } = useContext(ConfigContext);
  const {
    account: { hourFormat }
  } = useAccountConfig();

  const selectedBranchId = useRecoilValue(sharedAtoms.selectedBranch);
  const { mappedBranches } = useBranches();
  const defaultBranchCostCenterId =
    mappedBranches?.[selectedBranchId]?.scheduleConfig?.defaultBranchCostCenterId ?? null;

  const [employeeSelection, setEmployeeSelection] = useState(
    data?.employeeId ? { employee: employeesMap?.[data.employeeId], warnings: null } : null
  );
  const employeeData = employeeSelection?.employee;
  const employeeWarnings = employeeSelection?.warnings;
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showPositionWarning, setShowPositionWarning] = useState(null);
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [loadingConfirmModal, setLoadingConfirmModal] = useState(false);

  const [controlledErrors, setControlledErrors] = useState({});

  const { mappedPositions } = usePositions();
  const { mainLocationOptions = [] } = useLocations({ branchId: selectedBranchId });

  const [addEmployeeModal, setAddEmployeeModal] = useState(false);
  const [saveAsDraft] = useState(false);
  const { labelOptions } = useLabels();
  const { startDate, endDate } = useRecoilValue(schedulingTimeRange);

  const [selectedDays, setSelectedDays] = useState([]);

  const getSelectedDates = useCallback(() => Object.keys(selectedDays).filter((key) => !!selectedDays[key]), [
    selectedDays
  ]);

  const enableCopyShift = useMemo(() => !editMode && !!weekView, [editMode, weekView]);

  const initialValues = useMemo(() => {
    const defaultSelectedLocation = mainLocationOptions.length === 1 ? mainLocationOptions[0]?.value : null;
    const defaultShiftCostCenterId = editMode ? undefined : defaultBranchCostCenterId;

    return {
      startTime: data?.startTime,
      endTime: data?.endTime,
      date: data?.date,
      positionId: data?.positionId,
      employeeId: employeeData?.id,
      id: data?.shiftId || -Date.now(),
      clientBranchId: data?.clientBranchId || undefined,
      clientAdminId: data?.clientAdminId || undefined,
      labelId: data?.labelId || null,
      branchCostCenterId: data?.branchCostCenterId || defaultShiftCostCenterId || null,
      locationId: data?.locationId || defaultSelectedLocation,
      bookingOption: data?.bookingOption,
      copyShift: true,
      areaId: data?.areaId || null,
      clockIn: data?.clockIn || null,
      clockOut: data?.clockOut || null,
      breaktimes: data?.breaktimes || [],
      notes: data?.notes || null,
      needed: 1,
      isOpenShift: data?.isOpenShift,
      slotWarnings: data?.slotWarnings,
      bookedOutsideAvailability: data?.bookedOutsideAvailability,
      shiftTags: data?.shiftTags || []
    };
  }, [
    mainLocationOptions,
    data?.startTime,
    data?.endTime,
    data?.date,
    data?.positionId,
    data?.shiftId,
    data?.clientBranchId,
    data?.clientAdminId,
    data?.labelId,
    data?.branchCostCenterId,
    data?.locationId,
    data?.bookingOption,
    data?.areaId,
    data?.clockIn,
    data?.clockOut,
    data?.breaktimes,
    data?.notes,
    data?.isOpenShift,
    data?.slotWarnings,
    data?.bookedOutsideAvailability,
    data?.shiftTags,
    employeeData?.id,
    editMode,
    defaultBranchCostCenterId
  ]);

  const { projectId, shiftId, isWeeklyPlanner } = data || {};

  const openShiftsByDateEmployeePositionOverlapping = useMemo(() => {
    const chosenEmployeePositions = data?.employeeId ? employeesMap?.[data.employeeId]?.positions || [] : [];

    const employeesShifts = shiftsByEmployee?.[data?.employeeId] || [];
    const formattedDate = moment(data?.date, API_DATE_TIME_FORMAT).format(API_DATE_FORMAT);

    // here it is assuming we dont play with the date field
    const openShiftsByDateInDate = (openShiftsByDate || [])?.[formattedDate];
    if (!openShiftsByDateInDate) {
      return [];
    }

    return openShiftsByDateInDate
      ?.filter(({ positionId }) => chosenEmployeePositions.includes(positionId))
      .filter(({ startTime, endTime }) => {
        const newStart = moment(startTime, API_DATE_TIME_FORMAT);
        const newEnd = moment(endTime, API_DATE_TIME_FORMAT);

        return !checkOverlappingHours({ data: employeesShifts || [], newStart, newEnd, includeEdges: true });
      });
  }, [data?.date, data?.employeeId, employeesMap, openShiftsByDate, shiftsByEmployee]);

  const onSubmit = useCallback(
    async ({ values, form }) => {
      const daysToCopy = values?.copyShift ? getSelectedDates({ selectedDays }) : null;

      const breaktimes = values.breaktimes
        ? values.breaktimes.reduce(
            (output, current) => {
              if (current.status) {
                output[current.status].push(current.status === 'deleted' ? current.id : current);
              }

              return output;
            },
            { edited: [], added: [], deleted: [] }
          )
        : undefined;

      setLoadingSubmission(true);
      try {
        await handleSubmit?.({
          values: {
            ...values,
            breaktimes,
            projectId,
            shiftId,
            employeeId: employeeData?.id,
            saveAsDraft,
            daysToCopy,
            isOpenShift: false
          },
          form
        });
      } finally {
        setLoadingSubmission(false);
      }
    },
    [employeeData?.id, getSelectedDates, handleSubmit, projectId, saveAsDraft, selectedDays, shiftId]
  );

  // ---- DRAFT OF OPEN SHIFT ---
  // const renderButtons = useMemo(
  //   () =>
  //     showDraftOption
  //       ? ({ loadingConfirm }) => (
  //           <DraftButton
  //             disaled={loadingConfirm && saveAsDraft}
  //             isLoading={loadingConfirm && saveAsDraft}
  //             type="submit"
  //             secondary
  //             onClick={() => setSaveAsDraft(true)}
  //           />
  //         )
  //       : editMode
  //       ? () => <div />
  //       : undefined,
  //   [editMode, showDraftOption, saveAsDraft]
  // );

  const confirmText = useMemo(() => {
    if (employeeData) {
      return editMode ? t('update') : `${t('book')} ${employeeData.firstName}`;
    }

    if (editMode) {
      return employeeData ? t('update') : t('unbook');
    }

    return t('createOpenShift');
  }, [editMode, employeeData, t]);

  const employeeName = employeeData?.id
    ? `${employeesMap?.[employeeData.id]?.firstName} ${employeesMap?.[employeeData.id]?.lastName}`
    : '';

  const formattedDayDate = moment(data?.date).format(`ddd, ${dateFormat}`);

  const title = useMemo(
    () =>
      editMode && employeeData ? (
        <Trans
          i18nKey="editShiftOn"
          values={{
            date: formattedDayDate
          }}
          components={[<DateText key="edit-shifts-date-text" />]}
        />
      ) : employeeData ? (
        <Trans
          i18nKey="bookEmployeeFor"
          values={{
            employee: employeeName,
            date: formattedDayDate
          }}
          components={[<DateText key="book-employee-date-text" />]}
        />
      ) : (
        'openShifts'
      ),
    [editMode, employeeData, employeeName, formattedDayDate]
  );

  const handleSubmission = useCallback(
    async (values, form) => {
      const { positionId, employeeId } = values;

      const employeePositions = employeesMap?.[employeeId]?.positions || [];
      //   let positionId = selectedPositionId;

      // in cse we want to show all the existing ope nshifts not by positin

      //   if (!!openShiftId && bookingOption === TIME_AVAILABILITY_BOOKING_OPTIONS.BY_OPEN_SHIFTS) {
      //     const formattedDate = moment(values.date).format('YYYY-MM-DD');
      //     const { positionId: openShiftPositionId } = mappedOpenShiftsByDateAndId[formattedDate]?.[openShiftId] || {};
      //     positionId = openShiftPositionId;
      //   }

      if (employeeId && !employeePositions.includes(positionId)) {
        return setShowPositionWarning({ values, form });
      }

      onSubmit({ values, form });
    },
    [employeesMap, onSubmit]
  );

  const onConfirmPrompt = useCallback(async () => {
    setLoadingConfirmModal(true);
    try {
      await onSubmit(showPositionWarning);
      refetchEmployees?.();
    } finally {
      // Always reset loading state and close position warning modal
      // Whether successful or with compliance violation (handled by parent)
      setLoadingConfirmModal(false);
      setShowPositionWarning(false);
    }
  }, [onSubmit, refetchEmployees, showPositionWarning]);

  const validateForm = useCallback(
    (values) => {
      const errors = {};
      if (
        !editMode &&
        !isCreateModalOpen &&
        openShiftsByDateEmployeePositionOverlapping.length > 0 &&
        !values?.openShiftId
      ) {
        errors._error = 'Please select a shift';
      }

      return errors;
    },
    [editMode, isCreateModalOpen, openShiftsByDateEmployeePositionOverlapping.length]
  );

  return (
    <>
      <FormModal
        closeOnClickOutside
        onSubmit={handleSubmission}
        keepDirtyOnReinitialize
        size="smallSemiWideLong"
        onClose={onClose}
        initialValues={initialValues}
        showCancelButton
        $showDraftOption={showDraftOption}
        disabledOnPristine={false}
        disabledOnInvalid={true}
        disableLoadingConfirmButton={saveAsDraft}
        confirmText={confirmText}
        setControlledErrors={setControlledErrors}
        validate={validateForm}
        renderButtons={
          isCreateModalOpen
            ? undefined
            : () => (
                <Button
                  type="button"
                  tertiary
                  onClick={() => setIsCreateModalOpen(true)}
                  className="add-new-shift-button">
                  {t('addNewShift')}
                </Button>
              )
        }
        {...modalProps}
        loadingConfirm={modalProps?.loadingConfirm || loadingSubmission}
        mutators={{ ...arrayMutators }}
        title={modalProps?.title || title}
        $isAttentionBox={editMode && !isWeeklyPlanner}>
        {renderExtraData}

        <HourlyBookingFields
          shiftId={shiftId}
          isCreateModalOpen={isCreateModalOpen}
          setIsCreateModalOpen={setIsCreateModalOpen}
          errors={controlledErrors}
          timePickerOptions={timePickerOptions}
          onClose={onClose}
          startDate={startDate}
          endDate={endDate}
          hourFormat={hourFormat}
          data={initialValues}
          branchId={selectedBranchId}
          isPositionRequired
          mappedLabels={mappedLabels}
          showLabelField={showLabel}
          onDelete={onDelete}
          editMode={editMode}
          interval={interval}
          disableDateChange={disableDateChange}
          disableTimesChange={disableTimesChange}
          employee={employeeData}
          employeeWarnings={employeeWarnings}
          setEmployee={(selection) =>
            setEmployeeSelection(
              selection ? { employee: selection.employee ?? selection, warnings: selection.warnings ?? null } : null
            )
          }
          initialTimingValues={initialTimingValues}
          timeFormat={timeFormat}
          t={t}
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          setAddEmployeeModal={setAddEmployeeModal}
          enableCopyShift={enableCopyShift}
          relevantOpenShifts={openShiftsByDateEmployeePositionOverlapping}
          isWeeklyPlanner={isWeeklyPlanner}
          projectId={projectId}
          showLabel={labelOptions?.length > 1}
          bookedOutsideAvailability={data?.bookedOutsideAvailability}
          slotWarnings={data?.slotWarnings}
          showShiftTags={showShiftTags}
          eventTags={data?.eventTags}
        />
      </FormModal>

      {addEmployeeModal && (
        <EmployeeCard
          addProfile
          defaultData={addEmployeeModal}
          onSubmit={(emp) => setEmployeeSelection(emp ? { employee: emp, warnings: null } : null)}
          onClose={() => setAddEmployeeModal(false)}
        />
      )}

      {showPositionWarning && (
        <ConfirmationModal
          onClose={() => setShowPositionWarning(false)}
          title={t('areYouSure')}
          loadingConfirm={loadingConfirmModal}
          onConfirm={onConfirmPrompt}
          confirmText={t('ok')}
          usePortal
          showCancelButton>
          <div>
            <Trans
              i18nKey="addPositionToEmployee"
              values={{
                name: `${employeeData?.firstName} ${employeeData?.lastName}`,
                position: mappedPositions[showPositionWarning?.values?.positionId]?.label
              }}
              components={{ bold: <strong /> }}
            />
          </div>
        </ConfirmationModal>
      )}
    </>
  );
};

export default React.memo(HourlyBookingModal);
